import isEqual from 'lodash/isEqual'
import React, { ReactNode } from 'react'
import { Platform, ViewStyle } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { tva } from '../../tailwind/merge'
import { useMeasuredWidth } from '../../useMeasuredWidth'
import { Box } from '../box'
import { Pressable } from '../pressable'
import { Text } from '../text'
import { TextTone } from '../text/variants'

export interface OptionGridProps<T> {
    items: T[]
    value?: T | null
    onChange: (item: T) => void
    labelRenderer?: (item: T) => string
    valueRenderer?: (item: T, isSelected: boolean) => ReactNode
    columns?: number
    disabled?: boolean
    className?: string
}

const GAP_PX = 8

const optionGridItem = tva({
    base: 'items-center justify-center rounded border border-solid px-2 py-2',
    variants: {
        isMeasured: {
            true: '',
            false: 'grow',
        },
        isSelected: {
            true: 'border-action-default bg-action-default',
            false: 'border-border-subtle bg-transparent hover:border-border-strong',
        },
        isDisabled: {
            true: 'pointer-events-none opacity-40',
            false: '',
        },
    },
    defaultVariants: {
        isSelected: false,
        isDisabled: false,
    },
})

export const OptionGrid = <T,>({
    items,
    value,
    onChange,
    labelRenderer,
    valueRenderer,
    columns = items.length || 1,
    disabled = false,
    className,
}: OptionGridProps<T>) => {
    // Native cannot parse `calc`, so its tiles are sized from the measured row
    // width instead — but that width is 0 until the first `onLayout`/`ResizeObserver`
    // tick, so `web:` keeps the immediate, measurement-free `calc()` basis it had
    // before, and native falls back to `grow` (the old look, not a scrambled one)
    // for that first frame.
    const { width, props: measureProps } = useMeasuredWidth()
    const itemWidth = width > 0 ? (width - GAP_PX * (columns - 1)) / columns : undefined
    const basis = `calc(${100 / columns}% - ${(GAP_PX * (columns - 1)) / columns}px)`
    const isWeb = Platform.OS === 'web'
    const style = (
        isWeb ? { flexBasis: basis, maxWidth: basis } : { width: itemWidth }
    ) as ViewStyle

    return (
        <Box {...measureProps} className={twMerge('flex-row flex-wrap gap-2', className)}>
            {items.map((item, index) => {
                const isSelected = isEqual(item, value)
                const labelTone: TextTone = isSelected ? 'on-primary' : 'default'

                return (
                    <Pressable
                        key={index}
                        disabled={disabled}
                        onPress={() => onChange(item)}
                        accessibilityRole='radio'
                        accessibilityState={{ selected: isSelected, disabled }}
                        style={style}
                        className={optionGridItem({
                            isSelected,
                            isDisabled: disabled,
                            isMeasured: isWeb || itemWidth !== undefined,
                        })}
                    >
                        {valueRenderer ? (
                            valueRenderer(item, isSelected)
                        ) : (
                            <Text size='caption' tone={labelTone} numberOfLines={1}>
                                {labelRenderer ? labelRenderer(item) : String(item)}
                            </Text>
                        )}
                    </Pressable>
                )
            })}
        </Box>
    )
}

OptionGrid.displayName = 'OptionGrid'
